import { HoverPillTooltip } from '@components/ui/HoverPillTooltip';
import { useAuthStore } from '@store/authStore';
import { cn } from '@utils/cn';
import {
  BarChart3,
  BookOpen,
  ChevronDown,
  FileStack,
  KeyRound,
  LayoutDashboard,
  Library,
  Mail,
  Menu,
  Plus,
  Settings,
  ShieldCheck,
  Users,
  Users2,
  X
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { canViewPage, type PermissionPage } from '../../auth/permissions';

const navItems: Array<{ to: string; label: string; icon: any; page: PermissionPage }> = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, page: 'dashboard' },
  { to: '/campaigns', label: 'Phishing Sim...', icon: Mail, page: 'phishing_sim' },
  { to: '/training', label: 'Training', icon: BookOpen, page: 'training' },
  { to: '/content', label: 'Content Library', icon: Library, page: 'content_library' },
  { to: '/users', label: 'Users', icon: Users, page: 'users' },
  { to: '/groups', label: 'Groups', icon: Users2, page: 'groups' },
  { to: '/roles', label: 'Roles', icon: KeyRound, page: 'roles' },
  { to: '/reports', label: 'Reports', icon: BarChart3, page: 'reports' },
  { to: '/settings', label: 'Settings', icon: Settings, page: 'settings' },
];

interface SidebarProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

type TooltipState = {
  label: string;
  top: number;
} | null;

export function Sidebar({ collapsed, onCollapsedChange, isMobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [tooltip, setTooltip] = useState<TooltipState>(null);
  const [isPhishingExpanded, setIsPhishingExpanded] = useState(() =>
    location.pathname.startsWith('/campaigns')
  );
  const visibleNavItems = navItems.filter((item) => {
    if (item.page === 'phishing_sim') {
      return canViewPage(user, 'phishing_sim') || canViewPage(user, 'templates');
    }
    return canViewPage(user, item.page);
  });
  const canViewCampaigns = canViewPage(user, 'phishing_sim');
  const canCreateCampaign =
    user?.roles?.includes('super_admin') === true ||
    user?.permissions?.phishing_sim?.includes('create') === true;

  const showTooltip = (label: string, element: HTMLElement) => {
    if (!collapsed) return;

    const rect = element.getBoundingClientRect();
    setTooltip({
      label,
      top: rect.top + rect.height / 2,
    });
  };

  const isExpanded = isMobileOpen || !collapsed;

  return (
    <>
      {/* Mobile backdrop overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 transition-opacity md:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex h-screen flex-col bg-brand-900 text-white transition-all duration-200 ease-in-out',
          // Mobile state
          isMobileOpen ? 'translate-x-0 w-72 max-w-[85vw]' : '-translate-x-full md:translate-x-0',
          // Desktop state
          collapsed ? 'md:w-20' : 'md:w-64'
        )}
      >
        <div
          className={cn(
            'flex items-center border-b border-brand-700',
            !isExpanded ? 'h-16 justify-center px-3' : 'h-16 justify-between gap-3 px-5'
          )}
        >
          {isExpanded && (
            <div className="flex min-w-0 items-center gap-2">
              <ShieldCheck className="h-7 w-7 shrink-0 text-blue-300" />
              <span className="font-bold text-sm leading-tight tracking-tight">
                RD Phishing &amp; Simulation Tool
              </span>
            </div>
          )}

          {isMobileOpen ? (
            <button
              type="button"
              onClick={onMobileClose}
              className="rounded-lg p-2 text-blue-200 transition-colors hover:bg-brand-700 hover:text-white md:hidden"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onCollapsedChange(!collapsed)}
              onMouseEnter={(event) => showTooltip('Expand menu', event.currentTarget)}
              onFocus={(event) => showTooltip('Expand menu', event.currentTarget)}
              onMouseLeave={() => setTooltip(null)}
              onBlur={() => setTooltip(null)}
              className="rounded-lg p-2 text-blue-200 transition-colors hover:bg-brand-700 hover:text-white hidden md:block"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
              title={collapsed ? undefined : 'Collapse sidebar'}
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
        </div>

        <nav className="flex-1 space-y-1 px-3 py-4">
          {visibleNavItems.map(({ to, label, icon: Icon }) => {
            if (to === '/campaigns') {
              return (
                <div key={to}>
                  <button
                    type="button"
                    onClick={() => {
                      if (collapsed) {
                        navigate(canViewCampaigns ? '/campaigns' : '/campaigns/templates');
                        onMobileClose?.();
                      } else {
                        setIsPhishingExpanded((expanded) => !expanded);
                      }
                    }}
                    onMouseEnter={(event) => showTooltip(label, event.currentTarget)}
                    onFocus={(event) => showTooltip(label, event.currentTarget)}
                    onMouseLeave={() => setTooltip(null)}
                    onBlur={() => setTooltip(null)}
                    className={cn(
                      'flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      !isExpanded ? 'justify-center' : 'gap-3',
                      location.pathname.startsWith('/campaigns')
                        ? 'bg-brand-600 text-white'
                        : 'text-blue-200 hover:bg-brand-700 hover:text-white'
                    )}
                    aria-expanded={isExpanded && isPhishingExpanded}
                  >
                    <Icon className="h-4.5 w-4.5 shrink-0" />
                    {isExpanded && (
                      <>
                        <span className="truncate">{label}</span>
                        <ChevronDown className={cn(
                          'ml-auto h-4 w-4 transition-transform',
                          isPhishingExpanded && 'rotate-180'
                        )} />
                      </>
                    )}
                  </button>

                  {isExpanded && isPhishingExpanded && (
                    <div className="mt-1 space-y-1 pl-3">
                      {canViewCampaigns && (
                        <NavLink
                          to="/campaigns"
                          end
                          onClick={onMobileClose}
                          className={({ isActive }) => cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            isActive ? 'bg-brand-700 text-white' : 'text-blue-200 hover:bg-brand-700 hover:text-white'
                          )}
                        >
                          <Mail className="h-4 w-4 shrink-0" />
                          <span>Campaign Info</span>
                        </NavLink>
                      )}

                      {canCreateCampaign && (
                        <NavLink
                          to="/campaigns/create"
                          onClick={onMobileClose}
                          className={({ isActive }) => cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            isActive ? 'bg-brand-700 text-white' : 'text-blue-200 hover:bg-brand-700 hover:text-white'
                          )}
                        >
                          <Plus className="h-4 w-4 shrink-0" />
                          <span>Add Campaign</span>
                        </NavLink>
                      )}

                      {canViewPage(user, 'templates') && (
                        <NavLink
                          to="/campaigns/templates"
                          onClick={onMobileClose}
                          className={({ isActive }) => cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            isActive ? 'bg-brand-700 text-white' : 'text-blue-200 hover:bg-brand-700 hover:text-white'
                          )}
                        >
                          <FileStack className="h-4 w-4 shrink-0" />
                          <span>Templates</span>
                        </NavLink>
                      )}

                      {canViewPage(user, 'phishing_sim') && (
                        <NavLink
                          to="/campaigns/teachable-moments"
                          onClick={onMobileClose}
                          className={({ isActive }) => cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            isActive ? 'bg-brand-700 text-white' : 'text-blue-200 hover:bg-brand-700 hover:text-white'
                          )}
                        >
                          <BookOpen className="h-4 w-4 shrink-0" />
                          <span>Teachable Moments</span>
                        </NavLink>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={to}
                to={to}
                onClick={onMobileClose}
                onMouseEnter={(event) => showTooltip(label, event.currentTarget)}
                onFocus={(event) => showTooltip(label, event.currentTarget)}
                onMouseLeave={() => setTooltip(null)}
                onBlur={() => setTooltip(null)}
                className={({ isActive }) => {
                  const routeIsActive = to === '/reports'
                    ? isActive && !location.pathname.startsWith('/reports/threat-analyzer')
                    : isActive;

                  return cn(
                    'flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    !isExpanded ? 'justify-center' : 'gap-3',
                    routeIsActive
                      ? 'bg-brand-600 text-white'
                      : 'text-blue-200 hover:bg-brand-700 hover:text-white'
                  );
                }}
              >
                <Icon className="h-4.5 w-4.5 shrink-0" />
                {isExpanded && <span className="truncate">{label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {isExpanded && (
          <div className="border-t border-brand-700 px-5 py-3">
            <p className="text-xs text-blue-400">v1.0.0 · Routa Digital</p>
          </div>
        )}
      </aside>

      {collapsed && tooltip && (
        <HoverPillTooltip label={tooltip.label} left={96} top={tooltip.top} />
      )}
    </>
  );
}
